import { useState, useMemo, useEffect } from 'react'
import { IHttpError, useAttendanceBulkSettingMutation, useUpdateAttendanceSettingByCompanyIdMutation } from '@/@data'
import { CompanyLogo } from '@/components/common/CompanyLogo'
import { useNotification, useRouteQuery, useTranslator } from '@/hooks'
import {
  Box,
  Checkbox,
  Divider,
  IconButton,
  InputAdornment,
  Menu,
  MenuItem,
  TextField,
  Typography
} from '@mui/material'
import dayjs from 'dayjs'
import { TablePagination, TablePaginationProps } from '@/@core/components/mui/TablePagination'
import { SelectFieldLabel } from '@/components/common/input/SelectFieldLabel'
import { FormProvider, useForm, Controller } from 'react-hook-form'
import { Button } from '@/@core/components/mui/Button'
import { Link } from 'react-router-dom'
import { AttendanceFilter } from '../AttendanceFilter'

export const AttendanceTable = ({ data, onSuccess, meta }: { data: any; onSuccess: () => void; meta: any }) => {
  const { __, __n } = useTranslator()
  const n = useNotification()
  const { searchQuery } = useRouteQuery()

  // Get the base month from filter or current month
  const baseMonth = useMemo(() => {
    const filterMonth = searchQuery.get('month')

    return filterMonth ? dayjs(filterMonth) : dayjs()
  }, [searchQuery])

  const [editingRow, setEditingRow] = useState<string | null>(null)
  const [editValue, setEditValue] = useState<string>('')
  const [editWorkingType, setEditWorkingType] = useState<string>('')
  const [editingWorkingTypeRow, setEditingWorkingTypeRow] = useState<string | null>(null)
  const [selectionAnchorEl, setSelectionAnchorEl] = useState<null | HTMLElement>(null)
  const selectionOpen = Boolean(selectionAnchorEl)
  const [isAllChecked, setIsAllChecked] = useState<boolean>(false)
  const [excludedCompanies, setExcludedCompanies] = useState<Set<number>>(new Set())
  const [selectedCompanies, setSelectedCompanies] = useState<Set<number>>(new Set())
  const [visibleColumns, setVisibleColumns] = useState<string[]>([])
  const [isInitialized, setIsInitialized] = useState(false)

  // Check if at least one item has reconcile_1 status
  const hasReconcile1Data = useMemo(() => {
    return data.some((item: any) => item?.reconcile_1?.status !== null && item?.reconcile_1?.status !== undefined)
  }, [data])


  // Column key
  const COLUMN_KEYS = {
    EMPLOYEE: 'employer',
    MONTH_MINUS_2: 'month_minus_2',
    MONTH_MINUS_1: 'month_minus_1',
    CURRENT_MONTH: 'current_month',
    MONTH_PLUS_1: 'month_plus_1',
    WORKING_RATE: 'working_rate',
    WEEKLY_SCHEDULE: 'weekly_schedule',
    UPDATE_DATE: 'update_date'
  }

  const columns = useMemo(() => {
    const columnMap = [
      { key: COLUMN_KEYS.EMPLOYEE, label: __('Employee') },
      { key: COLUMN_KEYS.MONTH_MINUS_2, label: __('Month') + ' ' + baseMonth.subtract(2, 'month').format('MM/YYYY') },
      { key: COLUMN_KEYS.MONTH_MINUS_1, label: __('Month') + ' ' + baseMonth.subtract(1, 'month').format('MM/YYYY') },
      { key: COLUMN_KEYS.CURRENT_MONTH, label: __('Current Month') },
      ...(hasReconcile1Data ? [{ key: COLUMN_KEYS.MONTH_PLUS_1, label: __('Month') + ' ' + baseMonth.add(1, 'month').format('MM/YYYY') }] : []),
      { key: COLUMN_KEYS.WORKING_RATE, label: __('Working Rate') },
      { key: COLUMN_KEYS.WEEKLY_SCHEDULE, label: __('Weekly Work Schedule') },
      { key: COLUMN_KEYS.UPDATE_DATE, label: __('Update Date') }
    ]

    return columnMap
  }, [baseMonth, __, hasReconcile1Data])

  // Initialize visible columns
  useEffect(() => {
    if (!isInitialized && columns.length > 0) {
      setVisibleColumns(columns.map(col => col.key))
      setIsInitialized(true)
    }
  }, [columns, isInitialized])

  // Auto-add MONTH_PLUS_1 when hasReconcile1Data becomes true
  useEffect(() => {
    if (hasReconcile1Data && isInitialized) {
      setVisibleColumns(prev => {
        if (!prev.includes(COLUMN_KEYS.MONTH_PLUS_1)) {
          return [...prev, COLUMN_KEYS.MONTH_PLUS_1]
        }

        return prev
      })
    }
  }, [hasReconcile1Data, isInitialized])

  const isColumnVisible = (columnKey: string) => {
    return visibleColumns.includes(columnKey)
  }

  // Calculate selected count
  const getSelectedCount = () => {
    if (isAllChecked) {
      const totalCount = (meta as any)?.total || 0

      return totalCount - excludedCompanies.size
    } else {
      return selectedCompanies.size
    }
  }

  // Handle check all functionality
  const handleCheckAll = (checked: boolean) => {
    if (checked) {
      setIsAllChecked(true)
      setExcludedCompanies(new Set())
      setSelectedCompanies(new Set())
      methods.setValue('is_all', 1)
      methods.setValue('company_ids', [])
      methods.setValue('exclude_company_ids', [])
    } else {
      setIsAllChecked(false)
      setExcludedCompanies(new Set())
      setSelectedCompanies(new Set())
      methods.setValue('is_all', 0)
      methods.setValue('company_ids', [])
      methods.setValue('exclude_company_ids', [])
    }
  }

  // Handle individual checkbox
  const handleIndividualCheck = (id: number, checked: boolean) => {
    if (isAllChecked) {
      // flow 1: check all mode - manage excluded companies
      const newExcludedCompanies = new Set(excludedCompanies)

      if (!checked) {
        newExcludedCompanies.add(id)
      } else {
        newExcludedCompanies.delete(id)
      }

      setExcludedCompanies(newExcludedCompanies)

      const excludeArray = Array.from(newExcludedCompanies)

      methods.setValue('exclude_company_ids', excludeArray)
    } else {
      // flow 2: individual selection mode - manage selected companies
      const newSelectedCompanies = new Set(selectedCompanies)

      if (checked) {
        newSelectedCompanies.add(id)
      } else {
        newSelectedCompanies.delete(id)
      }

      setSelectedCompanies(newSelectedCompanies)

      const idsArray = Array.from(newSelectedCompanies)

      methods.setValue('company_ids', idsArray)
    }
  }

  const renderStatus = (status: string | null) => {
    if (!status) {
      return <Typography color='text.secondary'>-</Typography>
    }

    const statusConfig = {
      unreconciled: { color: '#ff4c51', label: __('Unreconciled') },
      reconciled: { color: '#28c76f', label: __('Reconciled') },
      checking: { color: '#3b82f6', label: __('Checking') }
    }

    const config = statusConfig[status as keyof typeof statusConfig] || statusConfig.unreconciled

    return (
      <div className='flex items-center gap-2'>
        <div className='w-2 h-2 rounded-full' style={{ backgroundColor: config.color }} />
        <Typography
          variant='body2'
          className='font-medium'
          style={{ color: status === 'unreconciled' ? '#ff4c51' : status === 'reconciled' ? '#28c76f' : '#3b82f6' }}
        >
          {config.label}
        </Typography>
      </div>
    )
  }

  const updateMutation = useUpdateAttendanceSettingByCompanyIdMutation()

  const handleSave = (rowId: any, workingType: any) => {
    updateMutation.mutate(
      {
        companyId: rowId,
        data: {
          working_rate: Number(editValue),
          working_type: workingType
        }
      },
      {
        onSuccess: () => {
          n.success(__('Working rate updated successfully'))
          setEditingRow(null)
          setEditValue('')
          onSuccess()
        },
        onError: (error: IHttpError) => {
          n.error(error.message)
        }
      }
    )
  }

  const handleInputChange = (value: string) => {
    setEditValue(value)
  }

  const handleCancel = () => {
    setEditingRow(null)
    setEditValue('')
  }

  const handleSettingsClick = (rowId: string, currentValue: number) => {
    setEditingRow(rowId)
    setEditValue(__n(currentValue) as string)
  }

  const workingTypeOptions = useMemo(() => {
    return [
      { label: __('Standard (Mon-Fri)'), value: 'standard' },
      { label: __('Extended (Mon-Sat)'), value: 'extended' },
      { label: __('Full Week (Mon-Sun)'), value: 'full_week' }
    ]
  }, [__])

  const bulkSettingMutation = useAttendanceBulkSettingMutation()

  const handleWorkingTypeSave = (rowId: any) => {
    const payload = {
      working_rate: Number(data.find((item: any) => item.id === rowId)?.working_rate),
      working_type: editWorkingType,
      company_ids: [rowId]
    }

    bulkSettingMutation.mutate(payload as any, {
      onSuccess: () => {
        n.success(__('Attendance settings updated successfully'))
        setEditingWorkingTypeRow(null)
        setEditWorkingType('')
        onSuccess()
      },
      onError: (error: IHttpError) => {
        n.error(error.message)
      }
    })
  }

  const handleWorkingTypeCancel = () => {
    setEditingWorkingTypeRow(null)
    setEditWorkingType('')
  }

  const handleWorkingTypeSettingsClick = (rowId: string, currentValue: string) => {
    setEditingWorkingTypeRow(rowId)
    setEditWorkingType(currentValue)
  }

  const methods = useForm<{
    working_rate: number
    working_type: string
    is_all: number
    company_ids: number[]
    exclude_company_ids: number[]
  }>({
    defaultValues: {
      working_rate: undefined,
      working_type: undefined,
      is_all: 0,
      company_ids: [],
      exclude_company_ids: []
    },
    mode: 'onChange'
  })

  const onSubmit = async (data: any) => {
    const payload = {
      ...data,
      working_rate: Number(data.working_rate),
      working_type: data.working_type
    }

    bulkSettingMutation.mutate(payload as any, {
      onSuccess: () => {
        n.success(__('Attendance settings updated successfully'))
        setSelectedCompanies(new Set())
        setExcludedCompanies(new Set())
        setIsAllChecked(false)
        methods.reset()
        onSuccess()
      },
      onError: (error: IHttpError) => {
        n.error(error.message)
      }
    })
  }

  return (
    <>
      <AttendanceFilter
        columns={columns}
        visibleColumns={visibleColumns}
        onSelectColumns={cols => {
          setVisibleColumns(cols)
        }}
      />
      <FormProvider {...methods}>
        <form onSubmit={methods.handleSubmit(onSubmit)}>
          <div>
            {getSelectedCount() > 0 && (
              <>
                <Divider />
                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    padding: '20px'
                  }}
                >
                  <Box className='flex items-center gap-2'>
                    <div>
                      <Button
                        variant='outlined'
                        onClick={e => {
                          setSelectionAnchorEl(e.currentTarget)
                        }}
                        endIcon={<i className='tabler-chevron-down text-gray-600' />}
                        sx={{
                          borderRadius: '20px',
                          borderColor: '#d0d0d0',
                          color: '#666',
                          textTransform: 'none',
                          '&:hover': {
                            borderColor: '#b0b0b0'
                          }
                        }}
                      >
                        <span className='text-sm font-medium'>
                          {getSelectedCount()} {__('Selected')}
                        </span>
                      </Button>
                      <Menu
                        anchorEl={selectionAnchorEl}
                        open={selectionOpen}
                        onClose={() => {
                          setSelectionAnchorEl(null)
                        }}
                        anchorOrigin={{
                          vertical: 'bottom',
                          horizontal: 'left'
                        }}
                        transformOrigin={{
                          vertical: 'top',
                          horizontal: 'left'
                        }}
                        sx={{
                          borderRadius: 2,
                          mt: 1,
                          minWidth: 200
                        }}
                      >
                        <MenuItem
                          onClick={() => {
                            handleCheckAll(true)
                            setSelectionAnchorEl(null)
                          }}
                          sx={{ fontSize: '14px' }}
                        >
                          {(meta as any)?.total || 0} {__('Selected')}
                        </MenuItem>
                        <MenuItem
                          onClick={() => {
                            handleCheckAll(false)
                            setSelectionAnchorEl(null)
                          }}
                          sx={{ fontSize: '14px' }}
                        >
                          {__('Un-Selected')}
                        </MenuItem>
                      </Menu>
                    </div>

                    <Controller
                      name='working_rate'
                      control={methods.control}
                      render={({ field }) => (
                        <TextField
                          {...field}
                          size='small'
                          label={__('Working rate')}
                          placeholder={__('Enter Rate Assumption Working')}
                          type='number'
                          className='rounded-lg w-[250px] h-[38px]'
                          sx={{
                            '& .MuiInputBase-root': {
                              borderRadius: '20px'
                            }
                          }}
                          slotProps={{
                            input: {
                              onInput: (e: any) => {
                                if (e.target.value < 0) {
                                  e.target.value = 0
                                }

                                if (e.target.value > 100) {
                                  e.target.value = 100
                                }
                              },
                              endAdornment: (
                                <InputAdornment position='end'>
                                  <i className='tabler-percentage text-[#2f2b3de6]' />
                                </InputAdornment>
                              )
                            }
                          }}
                          autoFocus
                        />
                      )}
                    />

                    <div className='custom-select-label-special'>
                      <Controller
                        name='working_type'
                        control={methods.control}
                        render={({ field }) => (
                          <SelectFieldLabel
                            {...field}
                            options={workingTypeOptions}
                            label={__('Weekly Work Schedule')}
                            className='w-[250px] h-[38px] '
                          />
                        )}
                      />
                    </div>
                  </Box>
                  <Box className='flex items-center gap-2'>
                    <Button
                      variant='contained'
                      color='primary'
                      type='submit'
                      startIcon={<i className='tabler-check' />}
                    >
                      {__('Submit')}
                    </Button>
                    <Button
                      variant='tonal'
                      color='secondary'
                      startIcon={<i className='tabler-x' />}
                      onClick={() => {
                        setSelectedCompanies(new Set())
                        setExcludedCompanies(new Set())
                        setIsAllChecked(false)
                        methods.reset()
                      }}
                    >
                      {__('Clear')}
                    </Button>
                  </Box>
                </Box>
              </>
            )}
          </div>

          <div>
            <Box sx={{ overflowX: 'auto' }}>
              <table
                style={{
                  width: '100%',
                  borderCollapse: 'collapse',
                  border: '1px solid #e0e0e0'
                }}
              >
                <thead>
                  <tr className='border-right-custom'>
                    <th
                      style={{
                        padding: '12px 16px',
                        textAlign: 'left',
                        borderBottom: '1px solid #e0e0e0',
                        fontWeight: '500',
                        fontSize: '13px'
                      }}
                    >
                      <Checkbox
                        checked={isAllChecked}
                        indeterminate={!isAllChecked && selectedCompanies.size > 0}
                        onChange={e => handleCheckAll(e.target.checked)}
                      />
                    </th>
                    <th
                      style={{
                        padding: '12px 16px',
                        textAlign: 'left',
                        borderBottom: '1px solid #e0e0e0',
                        fontWeight: '500',
                        fontSize: '13px',
                        textTransform: 'uppercase'
                      }}
                    >
                      {__('Employer')}
                    </th>
                    {isColumnVisible(COLUMN_KEYS.EMPLOYEE) && (
                      <th
                        style={{
                          padding: '12px 16px',
                          textAlign: 'left',
                          borderBottom: '1px solid #e0e0e0',
                          fontWeight: '500',
                          fontSize: '13px',
                          textTransform: 'uppercase'
                        }}
                      >
                        {__('Employee')}
                      </th>
                    )}
                    {isColumnVisible(COLUMN_KEYS.MONTH_MINUS_2) && (
                      <th
                        style={{
                          padding: '12px 16px',
                          textAlign: 'left',
                          borderBottom: '1px solid #e0e0e0',
                          fontWeight: '500',
                          fontSize: '13px',
                          textTransform: 'uppercase'
                        }}
                      >
                        {__('Month')} {baseMonth.subtract(2, 'month').format('MM/YYYY')}
                      </th>
                    )}
                    {isColumnVisible(COLUMN_KEYS.MONTH_MINUS_1) && (
                      <th
                        style={{
                          padding: '12px 16px',
                          textAlign: 'left',
                          borderBottom: '1px solid #e0e0e0',
                          fontWeight: '500',
                          fontSize: '13px',
                          textTransform: 'uppercase'
                        }}
                      >
                        {__('Month')} {baseMonth.subtract(1, 'month').format('MM/YYYY')}
                      </th>
                    )}
                    {isColumnVisible(COLUMN_KEYS.CURRENT_MONTH) && (
                      <th
                        style={{
                          padding: '12px 16px',
                          textAlign: 'left',
                          borderBottom: '1px solid #e0e0e0',
                          fontWeight: '500',
                          fontSize: '13px',
                          textTransform: 'uppercase'
                        }}
                      >
                        {__('Current Month')}
                      </th>
                    )}
                    {isColumnVisible(COLUMN_KEYS.MONTH_PLUS_1) && hasReconcile1Data && (
                      <th
                        style={{
                          padding: '12px 16px',
                          textAlign: 'left',
                          borderBottom: '1px solid #e0e0e0',
                          fontWeight: '500',
                          fontSize: '13px',
                          textTransform: 'uppercase'
                        }}
                      >
                        {__('Month')} {baseMonth.add(1, 'month').format('MM/YYYY')}
                      </th>
                    )}
                    {isColumnVisible(COLUMN_KEYS.WORKING_RATE) && (
                      <th
                        style={{
                          padding: '12px 16px',
                          textAlign: 'left',
                          borderBottom: '1px solid #e0e0e0',
                          fontWeight: '500',
                          fontSize: '13px',
                          textTransform: 'uppercase'
                        }}
                      >
                        {__('Working Rate')}
                      </th>
                    )}
                    {isColumnVisible(COLUMN_KEYS.WEEKLY_SCHEDULE) && (
                      <th
                        style={{
                          padding: '12px 16px',
                          textAlign: 'left',
                          borderBottom: '1px solid #e0e0e0',
                          fontWeight: '500',
                          fontSize: '13px',
                          textTransform: 'uppercase'
                        }}
                      >
                        {__('Weekly Work Schedule')}
                      </th>
                    )}
                    {isColumnVisible(COLUMN_KEYS.UPDATE_DATE) && (
                      <th
                        style={{
                          padding: '12px 16px',
                          textAlign: 'left',
                          borderBottom: '1px solid #e0e0e0',
                          fontWeight: '500',
                          fontSize: '13px',
                          textTransform: 'uppercase'
                        }}
                      >
                        {__('Update Date')}
                      </th>
                    )}
                    <th
                      style={{
                        padding: '12px 16px',
                        textAlign: 'left',
                        borderBottom: '1px solid #e0e0e0',
                        fontWeight: '500',
                        fontSize: '13px',
                        textTransform: 'uppercase'
                      }}
                    >
                      {__('Action')}
                    </th>
                  </tr>
                </thead>
                {data.length > 0 && (
                  <tbody>
                    {data.map((item: any, index: any) => (
                      <tr key={index}>
                        <td
                          style={{
                            padding: '12px 16px',
                            textAlign: 'left',
                            borderBottom: '1px solid #e0e0e0',
                            fontWeight: '500',
                            fontSize: '13px',
                            textTransform: 'uppercase'
                          }}
                        >
                          <Checkbox
                            checked={
                              isAllChecked
                                ? !excludedCompanies.has(Number(item?.id))
                                : selectedCompanies.has(Number(item?.id))
                            }
                            onChange={e => handleIndividualCheck(Number(item?.id), e.target.checked)}
                          />
                        </td>
                        <td
                          style={{
                            padding: '12px 16px',
                            textAlign: 'left',
                            borderBottom: '1px solid #e0e0e0',
                            fontWeight: '500',
                            fontSize: '13px'
                          }}
                        >
                          <div title={item?.name}>
                            <CompanyLogo
                              company={item}
                              link={`/cms/attendance/${item?.id}?month=${item?.current_pay_period}`}
                            />
                          </div>
                        </td>
                        {isColumnVisible(COLUMN_KEYS.EMPLOYEE) && (
                          <td
                            style={{
                              padding: '12px 16px',
                              textAlign: 'left',
                              borderBottom: '1px solid #e0e0e0',
                              fontWeight: '500',
                              fontSize: '13px'
                            }}
                          >
                            <div className='flex items-center gap-2'>
                              <i className='tabler-users text-[#3551F2]' />
                              <Typography className='text-[15px] leading-[22px] font-normal'>
                                {item?.total_user}
                              </Typography>
                            </div>
                          </td>
                        )}
                        {isColumnVisible(COLUMN_KEYS.MONTH_MINUS_2) && (
                          <td
                            style={{
                              padding: '12px 16px',
                              textAlign: 'left',
                              borderBottom: '1px solid #e0e0e0',
                              fontWeight: '500',
                              fontSize: '13px'
                            }}
                          >
                            <div className='w-[150px]'>{renderStatus(item?.reconcile_4?.status)}</div>
                          </td>
                        )}
                        {isColumnVisible(COLUMN_KEYS.MONTH_MINUS_1) && (
                          <td
                            style={{
                              padding: '12px 16px',
                              textAlign: 'left',
                              borderBottom: '1px solid #e0e0e0',
                              fontWeight: '500',
                              fontSize: '13px'
                            }}
                          >
                            <div className='w-[150px]'>{renderStatus(item?.reconcile_3?.status)}</div>
                          </td>
                        )}
                        {isColumnVisible(COLUMN_KEYS.CURRENT_MONTH) && (
                          <td
                            style={{
                              padding: '12px 16px',
                              textAlign: 'left',
                              borderBottom: '1px solid #e0e0e0',
                              fontWeight: '500',
                              fontSize: '13px'
                            }}
                          >
                            <div className='w-[150px]'>{renderStatus(item?.reconcile_2?.status)}</div>
                          </td>
                        )}
                        {isColumnVisible(COLUMN_KEYS.MONTH_PLUS_1) && hasReconcile1Data && (
                          <td
                            style={{
                              padding: '12px 16px',
                              textAlign: 'left',
                              borderBottom: '1px solid #e0e0e0',
                              fontWeight: '500',
                              fontSize: '13px'
                            }}
                          >
                            <div className='w-[150px]'>{renderStatus(item?.reconcile_1?.status)}</div>
                          </td>
                        )}
                        {isColumnVisible(COLUMN_KEYS.WORKING_RATE) && (
                          <td
                            style={{
                              padding: '12px 16px',
                              textAlign: 'left',
                              borderBottom: '1px solid #e0e0e0',
                              fontWeight: '500',
                              fontSize: '13px'
                            }}
                          >
                            <div className='flex items-center justify-between gap-2 w-[200px]'>
                              {editingRow === item?.id ? (
                                <div className='flex items-center gap-2 w-full'>
                                  <TextField
                                    size='small'
                                    value={editValue}
                                    onChange={e => handleInputChange(e.target.value)}
                                    type='number'
                                    sx={{ width: '100%', height: '30px', borderRadius: '8px' }}
                                    slotProps={{
                                      input: {
                                        onInput: (e: any) => {
                                          if (e.target.value < 0) {
                                            e.target.value = 0
                                          }

                                          if (e.target.value > 100) {
                                            e.target.value = 100
                                          }
                                        },
                                        endAdornment: (
                                          <InputAdornment position='end'>
                                            <IconButton
                                              size='small'
                                              onClick={() => handleSave(item?.id, item?.working_type)}
                                            >
                                              <svg
                                                width='24'
                                                height='24'
                                                viewBox='0 0 24 24'
                                                fill='none'
                                                xmlns='http://www.w3.org/2000/svg'
                                              >
                                                <rect
                                                  x='4'
                                                  y='4'
                                                  width='16'
                                                  height='16'
                                                  rx='2'
                                                  stroke='#28C76F'
                                                  strokeWidth='1.5'
                                                  strokeLinecap='round'
                                                  strokeLinejoin='round'
                                                />
                                                <path
                                                  d='M9 12L11 14L15 10'
                                                  stroke='#28C76F'
                                                  strokeWidth='1.5'
                                                  strokeLinecap='round'
                                                  strokeLinejoin='round'
                                                />
                                              </svg>
                                            </IconButton>
                                            <IconButton size='small' onClick={handleCancel}>
                                              <svg
                                                width='24'
                                                height='24'
                                                viewBox='0 0 24 24'
                                                fill='none'
                                                xmlns='http://www.w3.org/2000/svg'
                                              >
                                                <rect
                                                  x='4'
                                                  y='4'
                                                  width='16'
                                                  height='16'
                                                  rx='2'
                                                  stroke='#FF4C51'
                                                  strokeWidth='1.5'
                                                  strokeLinecap='round'
                                                  strokeLinejoin='round'
                                                />
                                                <path
                                                  d='M10.5303 9.46967C10.2374 9.17678 9.76256 9.17678 9.46967 9.46967C9.17678 9.76256 9.17678 10.2374 9.46967 10.5303L10.5303 9.46967ZM13.4697 14.5303C13.7626 14.8232 14.2374 14.8232 14.5303 14.5303C14.8232 14.2374 14.8232 13.7626 14.5303 13.4697L13.4697 14.5303ZM14.5303 10.5303C14.8232 10.2374 14.8232 9.76256 14.5303 9.46967C14.2374 9.17678 13.7626 9.17678 13.4697 9.46967L14.5303 10.5303ZM9.46967 13.4697C9.17678 13.7626 9.17678 14.2374 9.46967 14.5303C9.76256 14.8232 10.2374 14.8232 10.5303 14.5303L9.46967 13.4697ZM10 10L9.46967 10.5303L13.4697 14.5303L14 14L14.5303 13.4697L10.5303 9.46967L10 10ZM14 10L13.4697 9.46967L9.46967 13.4697L10 14L10.5303 14.5303L14.5303 10.5303L14 10Z'
                                                  fill='#FF4C51'
                                                />
                                              </svg>
                                            </IconButton>
                                          </InputAdornment>
                                        )
                                      }
                                    }}
                                    autoFocus
                                  />
                                </div>
                              ) : (
                                <>
                                  <Typography variant='body2'>{__n(item?.working_rate) || '-'} %</Typography>
                                  <IconButton
                                    size='small'
                                    onClick={() => handleSettingsClick(item?.id, item?.working_rate)}
                                  >
                                    <i className='tabler-settings text-[#2f2b3de6]' />
                                  </IconButton>
                                </>
                              )}
                            </div>
                          </td>
                        )}
                        {isColumnVisible(COLUMN_KEYS.WEEKLY_SCHEDULE) && (
                          <td
                            style={{
                              padding: '12px 16px',
                              textAlign: 'left',
                              borderBottom: '1px solid #e0e0e0',
                              fontWeight: '500',
                              fontSize: '13px'
                            }}
                          >
                            <div className='flex items-center justify-between gap-2 w-[200px]'>
                              {editingWorkingTypeRow === index ? (
                                <div className='flex items-center w-full'>
                                  <SelectFieldLabel
                                    options={workingTypeOptions}
                                    value={editWorkingType}
                                    onChange={e => setEditWorkingType(e.target.value as string)}
                                    className='w-full'
                                    size='small'
                                  />
                                  <IconButton size='small' onClick={() => handleWorkingTypeSave(item?.id)}>
                                    <svg
                                      width='24'
                                      height='24'
                                      viewBox='0 0 24 24'
                                      fill='none'
                                      xmlns='http://www.w3.org/2000/svg'
                                    >
                                      <rect
                                        x='4'
                                        y='4'
                                        width='16'
                                        height='16'
                                        rx='2'
                                        stroke='#28C76F'
                                        strokeWidth='1.5'
                                        strokeLinecap='round'
                                        strokeLinejoin='round'
                                      />
                                      <path
                                        d='M9 12L11 14L15 10'
                                        stroke='#28C76F'
                                        strokeWidth='1.5'
                                        strokeLinecap='round'
                                        strokeLinejoin='round'
                                      />
                                    </svg>
                                  </IconButton>
                                  <IconButton size='small' onClick={handleWorkingTypeCancel}>
                                    <svg
                                      width='24'
                                      height='24'
                                      viewBox='0 0 24 24'
                                      fill='none'
                                      xmlns='http://www.w3.org/2000/svg'
                                    >
                                      <rect
                                        x='4'
                                        y='4'
                                        width='16'
                                        height='16'
                                        rx='2'
                                        stroke='#FF4C51'
                                        strokeWidth='1.5'
                                        strokeLinecap='round'
                                        strokeLinejoin='round'
                                      />
                                      <path
                                        d='M10.5303 9.46967C10.2374 9.17678 9.76256 9.17678 9.46967 9.46967C9.17678 9.76256 9.17678 10.2374 9.46967 10.5303L10.5303 9.46967ZM13.4697 14.5303C13.7626 14.8232 14.2374 14.8232 14.5303 14.5303C14.8232 14.2374 14.8232 13.7626 14.5303 13.4697L13.4697 14.5303ZM14.5303 10.5303C14.8232 10.2374 14.8232 9.76256 14.5303 9.46967C14.2374 9.17678 13.7626 9.17678 13.4697 9.46967L14.5303 10.5303ZM9.46967 13.4697C9.17678 13.7626 9.17678 14.2374 9.46967 14.5303C9.76256 14.8232 10.2374 14.8232 10.5303 14.5303L9.46967 13.4697ZM10 10L9.46967 10.5303L13.4697 14.5303L14 14L14.5303 13.4697L10.5303 9.46967L10 10ZM14 10L13.4697 9.46967L9.46967 13.4697L10 14L10.5303 14.5303L14.5303 10.5303L14 10Z'
                                        fill='#FF4C51'
                                      />
                                    </svg>
                                  </IconButton>
                                </div>
                              ) : (
                                <>
                                  <Typography variant='body2'>
                                    {item?.working_type === 'standard'
                                      ? __('Standard (Mon-Fri)')
                                      : item?.working_type === 'extended'
                                        ? __('Extended (Mon-Sat)')
                                        : __('Full Week (Mon-Sun)')}
                                  </Typography>
                                  <IconButton
                                    size='small'
                                    onClick={() => handleWorkingTypeSettingsClick(index, item?.working_type)}
                                  >
                                    <i className='tabler-settings text-[#2f2b3de6]' />
                                  </IconButton>
                                </>
                              )}
                            </div>
                          </td>
                        )}
                        {isColumnVisible(COLUMN_KEYS.UPDATE_DATE) && (
                          <td
                            style={{
                              padding: '12px 16px',
                              textAlign: 'left',
                              borderBottom: '1px solid #e0e0e0',
                              fontWeight: '500',
                              fontSize: '13px'
                            }}
                          >
                            <div className='w-[100px]'>
                              {item?.updated_at ? dayjs(item?.updated_at).format('DD/MM/YYYY') : '-'}
                            </div>
                          </td>
                        )}
                        <td
                          style={{
                            padding: '12px 16px',
                            textAlign: 'left',
                            borderBottom: '1px solid #e0e0e0',
                            fontWeight: '500',
                            fontSize: '13px'
                          }}
                        >
                          <div>
                            <Link to={`/cms/attendance/${item?.id}?month=${item?.current_pay_period}`}>
                              <IconButton>
                                <i className='tabler-edit' />
                              </IconButton>
                            </Link>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                )}
                {data.length === 0 && (
                  <tbody>
                    <tr>
                      <td colSpan={visibleColumns.length + 2} style={{ textAlign: 'center' }}>
                        <Box sx={{ p: 5, textAlign: 'center' }}>
                          <div className='h-[200px] flex items-center justify-center rounded-md'>
                            <div className='text-[15px] font-normal text-[#2f2b3de6]'>{__('No data found')}</div>
                          </div>
                        </Box>
                      </td>
                    </tr>
                  </tbody>
                )}
              </table>
            </Box>
            {meta && (
              <TablePagination
                meta={
                  {
                    ...meta,
                    page: Number(searchQuery.get('page')) || 1
                  } as unknown as TablePaginationProps
                }
              />
            )}
          </div>
        </form>
      </FormProvider>
    </>
  )
}
